import React, { FC } from 'react';
import { Collapse } from 'reactstrap';
import styled from 'styled-components';
import _isUndefined from 'lodash/isUndefined';
import _isArray from 'lodash/isArray';
import _some from 'lodash/some';
import _every from 'lodash/every';
import _includes from 'lodash/includes';
import _map from 'lodash/map';
import { SelectOption } from '@minecraft.typings';
import { helpers } from '@minecraft.utils';
import { CheckboxListWithSearchProps } from '../shared/types';
import CheckboxItem from '../CheckboxItem';

const { getAllNestedOptions } = helpers;

const CheckboxListInner = styled.div<{
  $nestingLevel: number;
  $areSubOptionsExist: boolean;
  $areNestedItemsCollapsed: boolean;
}>`
  ${(props) =>
    props.$nestingLevel === 1 &&
    props.$areSubOptionsExist &&
    `
    border-bottom: 0.0625rem solid ${props.theme.colors.primary.ltGrey1};

    &:not(:first-child) {
      margin-top: 1rem;
    }
  `}

  ${(props) =>
    props.$nestingLevel === 2 &&
    props.$areSubOptionsExist &&
    `
    margin-top: 1rem;
  `}
`;

interface Props extends Partial<CheckboxListWithSearchProps> {
  nestingLevel?: number;
  selectedOptions: SelectOption[];
  disabledOptions: SelectOption[];
  collapsedParentOptions?: SelectOption[];
  toggleParentOptions?: (parentItem: SelectOption) => void;
  handleCheckboxItemSelect: (newOptions: SelectOption[]) => void;
}

const shouldItemBeSelected = (selectedOptions: SelectOption[], item: SelectOption): boolean => {
  const allNestedOptions = getAllNestedOptions(item.options);
  const selectedOptionsValue = _map(selectedOptions, 'value');

  if (
    !_isUndefined(item?.options) &&
    _every(allNestedOptions, (option) => _includes(selectedOptionsValue, option.value))
  ) {
    return true;
  }

  if (_isUndefined(item?.options) && _some(selectedOptions, { value: item.value })) {
    return true;
  }

  return false;
};

const shouldItemBeDisabled = (disabledOptions: SelectOption[], item: SelectOption): boolean => {
  const allNestedOptions = getAllNestedOptions(item.options);
  const disabledOptionsValue = _map(disabledOptions, 'value');

  if (
    !_isUndefined(item?.options) &&
    _every(allNestedOptions, (option) => _includes(disabledOptionsValue, option.value))
  ) {
    return true;
  }

  if (_isUndefined(item?.options) && _some(disabledOptions, { value: item.value })) {
    return true;
  }

  return false;
};

const shoudCheckboxBeEnabled = (nestingLevel: number, item: SelectOption): boolean => {
  return (
    _isUndefined(item.options) ||
    (nestingLevel > 1 && _isArray(item?.options) && item?.options?.length > 1) ||
    nestingLevel === 1
  );
};

const shoudMinusBeEnabled = (selectedOptions: SelectOption[], item: SelectOption): boolean => {
  const allNestedOptions = getAllNestedOptions(item.options);
  const selectedOptionsValue = _map(selectedOptions, 'value');

  return _some(allNestedOptions, (option) => _includes(selectedOptionsValue, option.value));
};

const CheckboxList: FC<Props> = ({
  nestingLevel = 1,
  options,
  selectedOptions,
  disabledOptions,
  collapsedParentOptions,
  toggleParentOptions,
  handleCheckboxItemSelect,
}) => {
  return _map(options, (item) => {
    const areNestedItemsCollapsed = _some(collapsedParentOptions, { value: item.value }) || nestingLevel > 1;
    const areSubOptionsExist = !_isUndefined(item.options);
    const isItemCountExist = !_isUndefined(item.count);
    const isItemDisabled = shouldItemBeDisabled(disabledOptions, item);

    return (
      <CheckboxListInner
        $nestingLevel={nestingLevel}
        $areSubOptionsExist={areSubOptionsExist}
        key={item.value || item.label}
      >
        <CheckboxItem
          isItemSelected={shouldItemBeSelected(selectedOptions, item)}
          isItemDisabled={isItemDisabled}
          isItemCountExist={isItemCountExist}
          areSubOptionsExist={areSubOptionsExist}
          checkboxEnable={shoudCheckboxBeEnabled(nestingLevel, item)}
          nestingLevel={nestingLevel}
          minusEnable={shoudMinusBeEnabled(selectedOptions, item)}
          item={item}
          onCheckboxItemSelect={handleCheckboxItemSelect}
          toggleParentOptions={toggleParentOptions}
        />
        {item?.options && (
          <Collapse isOpen={areNestedItemsCollapsed}>
            <CheckboxList
              nestingLevel={nestingLevel + 1}
              options={item?.options}
              selectedOptions={selectedOptions}
              disabledOptions={disabledOptions}
              handleCheckboxItemSelect={handleCheckboxItemSelect}
            />
          </Collapse>
        )}
      </CheckboxListInner>
    );
  });
};

export default CheckboxList;
